import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, GitMerge, LayoutList, RefreshCw, Upload } from "lucide-react";
import { drw, errText, exportUrl, waitingVerification } from "./api";
import { cacheIndex, cachedIndex } from "./offlineCache";
import SheetIndex from "./SheetIndex";
import SheetViewer from "./SheetViewer";
import UploadDialog from "./UploadDialog";
import FieldShopSets from "./FieldShopSets";
import FieldShopAssign from "./FieldShopAssign";
import LinkReview from "./LinkReview";
import GroupDialog from "./GroupDialog";
import ReimportDialog from "./ReimportDialog";

/** Project-level Drawings tab. Mounted by the Field app and the Ops app alike.
 *  `link` = { sheet, room, product, markup, from } from the URL; `onOpenFrs(room, product)` is
 *  the host's way back to the FRS view the user came from. */
// `me` is the signed-in person's NAME, used as the author label on new markups and plan
// installs. DRW-FIX-3 — it is NOT a permission input: who may delete a markup comes from the
// principal's email in drawings/permissions.js, never from this string.
export default function DrawingsTab({ projectId, link, onOpenFrs, me, canWrite = true, isField = false, onLinkConsumed, onScopesChange }) {
  const [index, setIndex] = useState(null);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState("");
  const [trail, setTrail] = useState([]);     // DRW-5 §2 — sheets left through a callout, for Back
  const switchSheet = (id, opts = {}) => {
    if (opts.push && active && id !== active) setTrail((t) => [...t.slice(-19), active]);
    else if (!opts.push) setTrail([]);
    setActive(id); setCompare(null); setDeep(null);
  };
  const goBack = () => { const prev = trail[trail.length - 1]; if (!prev) return; setTrail((t) => t.slice(0, -1)); setActive(prev); setCompare(null); };
  const [compare, setCompare] = useState(null);
  const [upload, setUpload] = useState(false);
  const [waiting, setWaiting] = useState(null);   // SHEET-NUM-1 — a set left mid-check
  const [deep, setDeep] = useState(null);
  const [shopAssign, setShopAssign] = useState(null);   // DRW-6 — assign rooms right after a shop upload
  const [shopTick, setShopTick] = useState(0);
  const [hasShop, setHasShop] = useState(false);
  const [working, setWorking] = useState(null);     // DRW-LOAD-A1 — a set the server is still processing
  const [links, setLinks] = useState(null);         // MARK-LINK-1 — marks not resolved to the FRS
  const [showLinks, setShowLinks] = useState(false);
  const [grouping, setGrouping] = useState(false);     // DRW-7
  const [expanded, setExpanded] = useState(false);     // DRW-9 — survives a sheet switch (the viewer remounts per sheet)
  const [switcherGroup, setSwitcherGroup] = useState("");   // DRW-9a — the group Prev/Next step through
  const [reimport, setReimport] = useState(false);     // TKO-3 §0
  useEffect(() => { drw.get(`/projects/${projectId}/field-shop/sets`).then(({ data }) => setHasShop(data.sets.length > 0)).catch(() => {}); }, [projectId, shopTick]);
  const uploadDone = (s) => {
    load(); setShopTick((n) => n + 1);
    if (s?.form?.kind === "field_shop" && s.set_id) drw.get(`/field-shop/sets/${s.set_id}`).then(({ data }) => setShopAssign(data)).catch(() => {});
  };

  const load = useCallback(async () => {
    try {
      const { data } = await drw.get(`/projects/${projectId}/sheets`, { params: { search } });
      setIndex(data);
      if (!search) cacheIndex(projectId, data).catch(() => {});
    } catch (e) {
      const c = await cachedIndex(projectId);
      if (c) setIndex({ ...c, offline: true }); else toast.error(errText(e, "Could not load drawings"));
    }
  }, [projectId, search]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
  }, [projectId, index?.current_set?.id]);

  // deep link (FRS → plan): resolve sheet + room + symbols once the index is here
  useEffect(() => {
    if (!index || !link || (!link.sheet && !link.room && !link.markup && !link.sheetId)) return;
    if (link.sheetId) {                 // DRW-6 — a field shop sheet opens directly, no lookup
      setActive(link.sheetId); setDeep(null); onLinkConsumed?.();
      return;
    }
    if (link.markup) {
      const m = link.markup;
      drw.get(`/projects/${projectId}/markups`, { params: { current_only: false } }).then(({ data }) => {
        const row = (data.markups || []).find((x) => x.id === m);
        if (row) { setActive(row.sheet_id); setDeep({ markupId: m }); }
      }).catch(() => {});
      onLinkConsumed?.();
      return;
    }
    drw.get(`/projects/${projectId}/lookup`, { params: { sheet: link.sheet || "", room: link.room || "", product: link.product || "" } })
      .then(({ data }) => {
        if (!data.found) { toast.message(link.sheet ? `No drawing for ${link.sheet}` : `Room ${link.room} is not on any current sheet`); return; }
        setActive(data.sheet_id);
        setDeep({ spaceId: data.space_id, markupIds: data.markup_ids, room: link.room, product: link.product });
      }).catch(() => {}).finally(() => onLinkConsumed?.());
    // eslint-disable-next-line
  }, [index?.current_set?.id, link?.sheet, link?.room, link?.product, link?.markup, link?.sheetId]);

  useEffect(() => {
    if (!active && index?.sheets?.length && !link?.sheetId) setActive(index.sheets[0].id);
  }, [index, active, link?.sheetId]);


  const sheets = useMemo(() => index?.sheets || [], [index]);
  // SHEET-NUM-1 — a set whose sheet numbers were left half-checked. Nothing is filed until it is
  // confirmed, so there has to be a way back in.
  useEffect(() => { waitingVerification(projectId).then(setWaiting).catch(() => setWaiting(null)); }, [projectId, shopTick]);
  // DRW-LOAD-A1 — processing happens on the server, so leaving the page is safe: coming back finds
  // the job and offers the progress again (or the retry, if it failed).
  useEffect(() => {
    let alive = true;
    const look = () => drw.get(`/projects/${projectId}/uploads/active`)
      .then(({ data }) => { if (alive) setWorking(data.active || null); })
      .catch(() => {});
    look();
    const t = setInterval(look, 5000);
    return () => { alive = false; clearInterval(t); };
  }, [projectId, shopTick]);
  // MARK-LINK-1 — the set's own resolution, so the unresolved values can be answered any time, not
  // only in the window that happened to be open after the upload.
  const setId = index?.current_set?.id || "";
  useEffect(() => {
    if (!setId) { setLinks(null); return; }
    drw.get(`/sets/${setId}/link-report`).then(({ data }) => setLinks(data)).catch(() => setLinks(null));
  }, [setId, shopTick]);
  if (!index) return <div className="p-6 text-sm text-neutral-500" data-testid="drawings-loading">Loading drawings…</div>;

  if (!index.current_set && !hasShop && !link?.sheetId) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-neutral-300 p-8 text-center space-y-3" data-testid="drawings-empty">
        <div className="text-lg font-semibold">No drawing set yet</div>
        <div className="text-sm text-neutral-600 max-w-lg mx-auto">Upload the Bluebeam Max set. Sheets split by page, Spaces become rooms, count markups become FRS symbols — nothing to retype.</div>
        {/* The FIRST set of a project files nothing until its numbers are confirmed, so the way back
            in has to live here too — not only once a set is already current. */}
        <div className="flex items-center justify-center gap-2 flex-wrap">
          {working && (
            <button type="button" onClick={() => setUpload({ watch: working.upload_id })}
                    className="h-8 px-2 text-xs rounded-md border border-orange-300 bg-orange-50 text-orange-900 font-medium inline-flex items-center gap-1"
                    data-testid="open-ingest-progress">
              {working.status === "failed" ? `${working.failed_step_label || "A step"} failed — carry on`
                : `${working.step_label || "Processing"}${working.total ? ` ${working.done}/${working.total}` : ""}…`}
            </button>
          )}
          {canWrite && waiting && (
            <button type="button" onClick={() => setUpload("resume")}
                    className="px-4 py-2 rounded-md border border-amber-400 bg-amber-50 text-amber-900 text-sm font-semibold"
                    data-testid="verify-resume">Finish checking {waiting.pages?.length || 0} sheet number(s){waiting.revision_label ? ` · ${waiting.revision_label}` : ""}</button>
          )}
          {canWrite && <button type="button" onClick={() => setUpload(true)} className="px-4 py-2 rounded-md bg-orange-500 text-white text-sm inline-flex items-center gap-2" data-testid="upload-first-set"><Upload className="w-4 h-4" /> Upload set</button>}
        </div>
        {upload && <UploadDialog resume={upload === "resume" ? waiting : null} watching={upload?.watch ? { upload_id: upload.watch } : null} projectId={projectId} guidance={GUIDE} hasSet={false} onDone={uploadDone} onClose={() => setUpload(false)} />}
      </div>
    );
  }

  const cs = index.current_set || { revision_label: "No architectural set", source: "", id: "" };
  return (
    <div className="space-y-2" data-testid="drawings-tab">
      <div className="flex items-center gap-2 flex-wrap text-sm">
        <span className="font-semibold" data-testid="current-set-label">{cs.revision_label}</span>
        {cs.issued_at && <span className="text-neutral-500 text-xs">issued {cs.issued_at.slice(0, 10)}</span>}
        <span className="text-xs text-neutral-500">{sheets.length} current sheets{cs.source ? ` · ${cs.source.replace("_", " ")}` : ""}</span>
        {cs.flattened && <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-800 font-medium" data-testid="set-flattened">flattened — markups not readable; re-upload unflattened</span>}
        {cs.linked_addendum_ref && <span className="text-xs px-2 py-0.5 rounded bg-neutral-100">↗ {cs.linked_addendum_ref}</span>}
        {index.offline && <span className="text-xs px-2 py-0.5 rounded bg-neutral-200">offline index</span>}
        <div className="ml-auto flex gap-1">
          {cs.id && <a href={exportUrl("sets", cs.id, { with_markups: true })} target="_blank" rel="noreferrer" className="h-8 px-2 text-xs rounded-md border flex items-center gap-1" data-testid="export-set"><Download className="w-3.5 h-3.5" /> Export set</a>}
          {cs.id && <a href={exportUrl("sets", cs.id, { with_markups: true, flatten: true })} target="_blank" rel="noreferrer" className="h-8 px-2 text-xs rounded-md border flex items-center gap-1" data-testid="export-set-flat"><Download className="w-3.5 h-3.5" /> Flat (GC print)</a>}
          {canWrite && waiting && (
            <button type="button" onClick={() => setUpload("resume")}
                    className="h-8 px-2 text-xs rounded-md border border-amber-400 bg-amber-50 text-amber-900 font-semibold"
                    data-testid="verify-resume">Finish checking {waiting.pages?.length || 0} sheet number(s){waiting.revision_label ? ` · ${waiting.revision_label}` : ""}</button>
          )}
          {cs.id && canWrite && (
            <button type="button" onClick={() => setGrouping(true)} className={`h-8 px-2 text-xs rounded-md border flex items-center gap-1 ${index.grouped ? "" : "border-neutral-400 font-medium"}`} data-testid="group-pages">
              <LayoutList className="w-3.5 h-3.5" /> {index.grouped ? "Re-group" : "Group"}
            </button>
          )}
          {cs.id && canWrite && cs.prior_sets?.length > 0 && (
            <button type="button" onClick={() => setReimport(true)} className={`h-8 px-2 text-xs rounded-md border flex items-center gap-1 ${cs.reimport_applied ? "" : "border-sky-400 bg-sky-50 text-sky-900 font-medium"}`} data-testid="reconcile-reimport">
              <GitMerge className="w-3.5 h-3.5" /> {cs.reimport_applied ? "Re-import record" : "Reconcile re-import"}
            </button>
          )}
          {links && (links.unresolved > 0 || links.linked > 0) && (
            <button type="button" onClick={() => setShowLinks(true)}
                    className={`h-8 px-2 text-xs rounded-md border font-medium ${links.unresolved ? "border-amber-400 bg-amber-50 text-amber-900" : "border-green-300 bg-green-50 text-green-900"}`}
                    data-testid="open-link-review">
              {links.unresolved ? `${links.unresolved} mark${links.unresolved === 1 ? "" : "s"} not linked` : `${links.linked} marks linked to the FRS`}
            </button>
          )}
          {working && (
            <button type="button" onClick={() => setUpload({ watch: working.upload_id })}
                    className="h-8 px-2 text-xs rounded-md border border-orange-300 bg-orange-50 text-orange-900 font-medium inline-flex items-center gap-1"
                    data-testid="open-ingest-progress-toolbar">
              {working.status === "failed" ? `${working.failed_step_label || "A step"} failed — carry on`
                : `${working.step_label || "Processing"}${working.total ? ` ${working.done}/${working.total}` : ""}…`}
            </button>
          )}
          {canWrite && <button type="button" onClick={() => setUpload(true)} className="h-8 px-2 text-xs rounded-md bg-orange-500 text-white flex items-center gap-1" data-testid="upload-revision"><Upload className="w-3.5 h-3.5" /> {cs.id ? "Upload revision" : "Upload"}</button>}
        </div>
      </div>
      {showLinks && setId && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-3" data-testid="link-review-dialog">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] overflow-auto p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="font-semibold">Takeoff marks and the FRS</div>
              <button type="button" onClick={() => { setShowLinks(false); drw.get(`/sets/${setId}/link-report`).then(({ data }) => setLinks(data)).catch(() => {}); }}
                      className="h-8 px-3 rounded-md border text-sm" data-testid="link-review-close">Close</button>
            </div>
            <LinkReview setId={setId} />
          </div>
        </div>
      )}
      <FieldShopSets projectId={projectId} activeId={active} onOpenSheet={(id) => switchSheet(id)} canWrite={canWrite && !isField} refreshKey={shopTick} />
      <div className="grid grid-cols-1 lg:grid-cols-[15rem_minmax(0,1fr)] gap-3">
        <div className="hidden lg:block h-[calc(100vh-11rem)] min-h-[520px]">
          <SheetIndex sheets={sheets} activeId={active} onOpen={(id) => { setActive(id); setCompare(null); setDeep(null); }} onChanged={load} search={search} setSearch={setSearch} grouped={!!index.grouped} />
        </div>
        <div className="lg:hidden">
          <select value={active} onChange={(e) => { setActive(e.target.value); setCompare(null); }} className="w-full h-10 border rounded-md px-2 text-sm" data-testid="sheet-jump">
            {sheets.map((s) => <option key={s.id} value={s.id}>{s.sheet_number} — {s.title}</option>)}
          </select>
        </div>
        <div className={`min-w-0 ${compare ? "grid grid-cols-1 xl:grid-cols-2 gap-2" : ""}`}>
          {active && (
            <SheetViewer key={active} projectId={projectId} sheetId={active} onSwitchSheet={switchSheet} onBack={trail.length ? goBack : null}
                         deepLink={deep} onOpenFrs={onOpenFrs} me={me} canWrite={canWrite} isField={isField}
                         compareWith={compare} onCompare={setCompare}
                         sheets={sheets} expanded={expanded} onExpand={setExpanded} linkScopes={link?.scopes || []} onScopesChange={onScopesChange}
                         activeGroup={switcherGroup} onGroupPicked={(g) => setSwitcherGroup(g || "")} />
          )}
          {compare && (
            <div data-testid="compare-pane">
              <SheetViewer key={`cmp-${compare}`} projectId={projectId} sheetId={compare} onSwitchSheet={setCompare} deepLink={null}
                           onOpenFrs={onOpenFrs} me={me} canWrite={false} isField={isField} compareWith={null} onCompare={() => setCompare(null)} />
            </div>
          )}
        </div>
      </div>
      {upload && <UploadDialog resume={upload === "resume" ? waiting : null} watching={upload?.watch ? { upload_id: upload.watch } : null} projectId={projectId} guidance={GUIDE} hasSet={!!index.current_set} onDone={uploadDone} onClose={() => setUpload(false)} />}
      {grouping && cs.id && <GroupDialog setId={cs.id} onApplied={load} onClose={() => setGrouping(false)} />}
      {reimport && cs.id && <ReimportDialog setId={cs.id} onApplied={load} onClose={() => setReimport(false)} />}
      {shopAssign && <FieldShopAssign set={shopAssign} onChanged={() => setShopTick((n) => n + 1)} onClose={() => { setShopAssign(null); setShopTick((n) => n + 1); }} />}
    </div>
  );
}

const GUIDE = [
  "Export UNFLATTENED from Bluebeam Max — markups must stay live annotations.",
  "Define a Space per room, named exactly as the FRS room number (e.g. 1101).",
  "Count markups: set Subject to the product acronym / FRS code (e.g. B-2888).",
  "Leave revision clouds on ASI sheets — they drive the change flags here.",
];
